/** "Add to dataset" for trace runs: each ticked trace's input and answer become a dataset item. */

import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { Select } from "@/components/ui/select";
import { errorText } from "@/components/ui/states";
import { useDatasets } from "@/features/datasets/hooks";
import { plural } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { useAddToDataset } from "../hooks";
import type { ToDatasetResult } from "../types";

function Done({
  name,
  result,
  id,
  onClose,
}: {
  name: string;
  result: ToDatasetResult;
  id: string;
  onClose: () => void;
}) {
  return (
    <Dialog
      title="Added to dataset"
      onClose={onClose}
      width={480}
      footer={
        <>
          <span className="flex-1" />
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <p className="text-[13px]">{summary(name, result)}</p>
      <Link className="link text-[13px]" to={`/datasets/${id}`}>
        Open {name}
      </Link>
    </Dialog>
  );
}

export function summary(name: string, r: ToDatasetResult): string {
  const version = r.dataset_version !== null ? ` v${r.dataset_version}` : "";
  const missing = r.missing.length
    ? ` · ${plural(r.missing.length, "trace")} no longer exist${r.missing.length === 1 ? "s" : ""}`
    : "";
  return `Added ${plural(r.added, "item")} to ${name}${version}${missing}`;
}

export function AddToDatasetDialog({
  experimentId,
  itemIds,
  onClose,
  onAdded,
}: {
  experimentId: string;
  itemIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const datasets = useDatasets();
  const [datasetId, setDatasetId] = useState("");
  const [result, setResult] = useState<ToDatasetResult | null>(null);
  const add = useAddToDataset(experimentId);
  const notify = useUiStore((s) => s.notify);
  const name = datasets.data?.find((d) => d.id === datasetId)?.name ?? "the dataset";

  if (result) return <Done name={name} result={result} id={datasetId} onClose={onClose} />;
  const submit = () =>
    add.mutate(
      { item_result_ids: itemIds, dataset_id: datasetId },
      {
        onSuccess: (r) => {
          setResult(r);
          notify(summary(name, r));
          onAdded();
        },
        onError: (e) => notify(errorText(e, "Could not add the traces."), "error"),
      },
    );

  return (
    <Dialog
      title="Add to dataset"
      subtitle={plural(itemIds.length, "trace") + " selected"}
      onClose={onClose}
      width={520}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!datasetId || add.isPending} onClick={submit}>
            Add to dataset
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-muted">
        Each trace's input and answer become a dataset item. Add the expected answer later.
      </p>
      <Field label="Dataset" htmlFor="to-dataset">
        <Select
          id="to-dataset"
          value={datasetId}
          onChange={setDatasetId}
          placeholder="Choose a dataset"
          options={(datasets.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
        />
      </Field>
    </Dialog>
  );
}
